import React, { useEffect, useRef } from 'react';
import { Menu } from '@headlessui/react';
import cx from 'classnames';

import { IconCaret } from '..';
import { getBorderClasses, getIconFillColor } from './helpers';
import {
    borderShim,
    defaultBorderConfig,
    defaultDisabled,
    defaultSize,
    defaultHeights,
    defaultWidths,
    descriptionStyles,
    headerLeftPadding,
    iconWidths,
} from './constants';

import './dropdown.css';

/**
 * A dropdown menu which leaves state management and child rendering to its parent component.
 * @param {DropdownWrapperProps} props
 */
export const DropdownWrapper = (props: DropdownWrapperProps): JSX.Element => {
    const {
        borderConfig = defaultBorderConfig,
        boxShadowWhenClosed = false,
        boxShadowWhenOpen = true,
        children,
        closeOnClickOutside = true,
        description,
        disabled = defaultDisabled,
        expansionDirection = 'down',
        handleCloseOnClickOutside,
        isExpanded,
        leftPadding = true,
        selectedLabel,
        setIsExpanded,
        showToggle = true,
        size = defaultSize,
    } = props;
    const descriptionStyle = descriptionStyles[size];
    const height = props.height ?? defaultHeights[size];
    const width = props.width ?? defaultWidths[size];
    const listHeight =
        props.listHeight !== undefined
            ? props.listHeight
            : (children?.length ?? 1) * height;
    const borderClasses = props.borderClasses
        ? props.borderClasses
        : getBorderClasses({
              borderConfig,
              size,
          });
    const iconFillColor = getIconFillColor({
        disabled,
        size,
    });
    const iconWidth = props.caretWidth ?? iconWidths[size];
    const headerLeftPaddingClass = headerLeftPadding[size];
    const parentElementRef = useRef<HTMLDivElement>(null);

    // Close dropdown when user clicks outside of it
    const clickListener = (e: Event): void => {
        const clickedElement = e.target as HTMLElement;

        if (
            closeOnClickOutside &&
            parentElementRef.current &&
            e.target &&
            !parentElementRef.current.contains(clickedElement) &&
            isExpanded
        ) {
            setIsExpanded(false);

            if (handleCloseOnClickOutside) {
                handleCloseOnClickOutside();
            }
        }
    };

    useEffect(() => {
        document.addEventListener('click', clickListener);

        return (): void => {
            document.removeEventListener('click', clickListener);
        };
    });

    return (
        <div
            className={cx(
                'dropdown-v2 z-20 relative inline-block text-left',
                size,
                {
                    'cursor-default': disabled,
                },
            )}
            style={{ height, width }}
            ref={parentElementRef}
        >
            <Menu>
                {(): JSX.Element => (
                    <>
                        {showToggle && selectedLabel && (
                            <Menu.Button
                                disabled={disabled}
                                className={cx(
                                    'dropdown-v2-header w-full h-full flex items-center justify-between',
                                    {
                                        'cursor-default': disabled,
                                        'shadow-lg':
                                            (!isExpanded &&
                                                boxShadowWhenClosed) ||
                                            (isExpanded && boxShadowWhenOpen),
                                        [headerLeftPaddingClass]: leftPadding,
                                    },
                                    borderClasses,
                                )}
                                onClick={(): void => {
                                    setIsExpanded(!isExpanded);
                                }}
                            >
                                {description && (
                                    <div
                                        className={`${descriptionStyle} mr-4 flex-none`}
                                    >
                                        {description}
                                    </div>
                                )}
                                <div
                                    className={cx(
                                        'truncate flex-grow text-left font-semibold',
                                        {
                                            'text-tertiary-400': disabled,
                                        },
                                    )}
                                >
                                    {selectedLabel}
                                </div>
                                <IconCaret
                                    width={iconWidth}
                                    fillColor={iconFillColor}
                                    rotation={
                                        expansionDirection === 'down' ? 0 : 180
                                    }
                                />
                            </Menu.Button>
                        )}

                        {isExpanded && (
                            <Menu.Items
                                static
                                className={cx(
                                    'dropdown-v2-menu w-full absolute left-0 outline-none',
                                    borderClasses,
                                    size,
                                    {
                                        'shadow-lg': boxShadowWhenOpen,
                                    },
                                )}
                                style={{
                                    top:
                                        expansionDirection === 'up' &&
                                        isExpanded
                                            ? -listHeight - borderShim
                                            : undefined,
                                }}
                            >
                                {children}
                            </Menu.Items>
                        )}
                    </>
                )}
            </Menu>
        </div>
    );
};
